import { z } from 'zod';
import { CLASS_KINDS } from './attendance';

export const ROOM_KINDS = ['amphi', 'salle', 'labo'] as const;
export type RoomKind = (typeof ROOM_KINDS)[number];

export const RoomSchema = z.object({
  id: z.uuid(),
  code: z.string(),
  name: z.string(),
  kind: z.enum(ROOM_KINDS),
  capacity: z.number().int(),
});
export type Room = z.infer<typeof RoomSchema>;

const Hhmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'timetable.errors.time');

/** Recurring weekly slot of one ECUE for one group during a semester (ScrTimetable « Nouvelle séance »). */
export const SlotInputSchema = z
  .object({
    academicYearId: z.uuid(),
    parity: z.enum(['odd', 'even']),
    groupId: z.uuid(),
    courseId: z.uuid(),
    teacherId: z.uuid().nullable(),
    roomId: z.uuid().nullable(),
    kind: z.enum(CLASS_KINDS),
    /** ISO weekday, 1 = Monday. */
    weekday: z.number().int().min(1).max(7),
    startTime: Hhmm,
    endTime: Hhmm,
  })
  .refine((s) => s.endTime > s.startTime, { path: ['endTime'], message: 'timetable.errors.endBeforeStart' });
export type SlotInput = z.infer<typeof SlotInputSchema>;

export const SlotSchema = z.object({
  id: z.uuid(),
  academicYearId: z.uuid(),
  parity: z.enum(['odd', 'even']),
  groupId: z.uuid(),
  courseId: z.uuid(),
  teacherId: z.uuid().nullable(),
  roomId: z.uuid().nullable(),
  kind: z.enum(CLASS_KINDS),
  weekday: z.number().int(),
  startTime: z.string(),
  endTime: z.string(),
  /** Joint session shared with other groups of the level (same teacher, same time). */
  linkId: z.uuid().nullable(),
});
export type Slot = z.infer<typeof SlotSchema>;

/** Slot with its joint groups and who taught it so far (a teacher change keeps the past sessions). */
export const SlotDetailSchema = SlotSchema.extend({
  linked: z.array(z.object({ slotId: z.uuid(), groupLabel: z.string() })),
  teacherHistory: z.array(
    z.object({
      teacherId: z.uuid().nullable(),
      teacherName: z.string().nullable(),
      from: z.string(),
      to: z.string(),
      sessions: z.number().int(),
    }),
  ),
});
export type SlotDetail = z.infer<typeof SlotDetailSchema>;

export const CONFLICT_KINDS = ['room', 'teacher', 'group'] as const;
export type ConflictKind = (typeof CONFLICT_KINDS)[number];

/** A clash between two recurring slots (or two sessions of the displayed week). */
export const ConflictSchema = z.object({
  kind: z.enum(CONFLICT_KINDS),
  weekday: z.number().int(),
  startTime: z.string(),
  endTime: z.string(),
  /** Shared resource label: room code, teacher name or group label. */
  resource: z.string(),
  a: z.object({ slotId: z.uuid(), group: z.string(), course: z.string() }),
  b: z.object({ slotId: z.uuid(), group: z.string(), course: z.string() }),
  /** Room conflicts: free rooms at that time, big enough for group A (best first). */
  freeRooms: z.array(RoomSchema),
});
export type Conflict = z.infer<typeof ConflictSchema>;

export const SlotCheckResultSchema = z.object({
  conflicts: z.array(ConflictSchema),
  /** Same teacher, same time, same level: saved as one joint session with these groups. */
  joint: z.array(z.object({ slotId: z.uuid(), groupLabel: z.string() })),
  /** Same teacher at an overlapping time in another level: refused (not forceable). */
  blocked: z.object({ group: z.string(), course: z.string() }).nullable(),
});
export type SlotCheckResult = z.infer<typeof SlotCheckResultSchema>;

export const TimetableQuerySchema = z.object({
  mode: z.enum(['group', 'teacher', 'room']),
  id: z.uuid(),
  /** Any day of the week to show (yyyy-mm-dd); defaults to the current week. */
  week: z.iso.date().optional(),
});
export type TimetableQuery = z.infer<typeof TimetableQuerySchema>;

export const TimetableEntrySchema = z.object({
  /** Class session of that week (null when the slot generated nothing, e.g. outside the semester). */
  sessionId: z.uuid().nullable(),
  slotId: z.uuid().nullable(),
  date: z.string(),
  weekday: z.number().int(),
  startTime: z.string(),
  endTime: z.string(),
  kind: z.enum(CLASS_KINDS),
  courseId: z.uuid(),
  courseName: z.string(),
  groupId: z.uuid(),
  groupLabel: z.string(),
  teacherId: z.uuid().nullable(),
  teacherName: z.string().nullable(),
  roomId: z.uuid().nullable(),
  room: z.string().nullable(),
  conflict: z.boolean(),
  rollCallDone: z.boolean(),
  /** The teacher was absent: the session did not take place (no roll call). */
  teacherAbsent: z.boolean(),
  /** Other groups of the same joint session. */
  linkedGroups: z.array(z.string()),
});
export type TimetableEntry = z.infer<typeof TimetableEntrySchema>;

export const TimetableWeekSchema = z.object({
  monday: z.string(),
  isoWeek: z.number().int(),
  parity: z.enum(['odd', 'even']).nullable(),
  weekdays: z.array(z.number().int()),
  slots: z.array(z.object({ start: z.string(), end: z.string() })),
  entries: z.array(TimetableEntrySchema),
  /** Conflicts involving the displayed entity (recurring slots of the semester). */
  conflicts: z.array(ConflictSchema),
});
export type TimetableWeek = z.infer<typeof TimetableWeekSchema>;

export const TimetableOptionsSchema = z.object({
  yearId: z.uuid(),
  groups: z.array(
    z.object({
      id: z.uuid(),
      label: z.string(),
      levelId: z.uuid(),
      size: z.number().int(),
      slots: z.number().int(),
    }),
  ),
  teachers: z.array(z.object({ id: z.uuid(), name: z.string() })),
  rooms: z.array(RoomSchema),
  /** ECUE of each group's semester (odd / even), for the slot dialog; teaching window of the study plan. */
  courses: z.array(
    z.object({
      id: z.uuid(),
      name: z.string(),
      levelId: z.uuid(),
      parity: z.enum(['odd', 'even']),
      startsOn: z.string().nullable(),
      endsOn: z.string().nullable(),
    }),
  ),
  /** Teacher of each course × group (grade sheets of the year): default of the drag-and-drop tags. */
  assignments: z.array(z.object({ groupId: z.uuid(), courseId: z.uuid(), teacherId: z.uuid() })),
});
export type TimetableOptions = z.infer<typeof TimetableOptionsSchema>;

export const SlotSaveSchema = SlotInputSchema.and(z.object({ force: z.boolean().default(false) }));

/** All groups of the year on one screen, for one day (« Vue d'ensemble »). */
export const TimetableOverviewQuerySchema = z.object({ yearId: z.uuid(), date: z.iso.date().optional() });
export const TimetableOverviewSchema = z.object({
  date: z.string(),
  parity: z.enum(['odd', 'even']).nullable(),
  weekdays: z.array(z.number().int()),
  slots: z.array(z.object({ start: z.string(), end: z.string() })),
  groups: z.array(z.object({ id: z.uuid(), label: z.string(), levelId: z.uuid() })),
  entries: z.array(TimetableEntrySchema),
});
export type TimetableOverview = z.infer<typeof TimetableOverviewSchema>;

export const TeacherAbsenceSchema = z.object({ absent: z.boolean() });
export type SlotSave = z.input<typeof SlotSaveSchema>;

export const SessionRoomSchema = z.object({ roomId: z.uuid() });

// ── Announcements ───────────────────────────────────────────────────────────
export const ANNOUNCEMENT_KINDS = ['info', 'event', 'warning', 'room'] as const;
export type AnnouncementKind = (typeof ANNOUNCEMENT_KINDS)[number];
export const AnnouncementInputSchema = z.object({
  title: z.string().trim().min(3).max(120),
  body: z.string().trim().min(3).max(1000),
  kind: z.enum(ANNOUNCEMENT_KINDS),
  /** null = everyone; otherwise a programme, level or group. */
  audience: z.discriminatedUnion('type', [
    z.object({ type: z.literal('all') }),
    z.object({ type: z.literal('program'), id: z.uuid() }),
    z.object({ type: z.literal('level'), id: z.uuid() }),
    z.object({ type: z.literal('group'), id: z.uuid() }),
  ]),
});
export type AnnouncementInput = z.infer<typeof AnnouncementInputSchema>;
export const AnnouncementSchema = z.object({
  id: z.uuid(),
  title: z.string(),
  body: z.string(),
  kind: z.enum(ANNOUNCEMENT_KINDS),
  audienceType: z.enum(['all', 'program', 'level', 'group']),
  audienceLabel: z.string().nullable(),
  authorName: z.string().nullable(),
  publishedAt: z.string(),
});
export type Announcement = z.infer<typeof AnnouncementSchema>;
